import type { ResultJSON } from '@bany/curl-to-json'
import type { HttpRequestNodeData } from 'v2/pages/flow/types'
import { logger } from 'v2/utils/logger'

// JSON.parse that returns `fallback` instead of throwing. Load transforms must not throw: the
// inverse transform would return a blank draft that a save then commits over the real agent.
export const parseJsonValue = <T>(raw: string | undefined | null, fallback: T): T => {
  if (typeof raw !== 'string' || !raw.trim()) return fallback
  try {
    return JSON.parse(raw) as T
  } catch (error) {
    logger.warn('Error parsing JSON data', { error })
    return fallback
  }
}

export const parseJsonData = (jsonString: string | undefined | null): Record<string, string> => {
  try {
    // Handle null, undefined, or non-string values
    if (!jsonString || typeof jsonString !== 'string' || !jsonString.trim()) {
      return {}
    }

    const parsed = JSON.parse(jsonString)

    // Ensure the parsed result is a plain object, not an array or primitive
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      return parsed
    }

    return {}
  } catch (error) {
    logger.warn('Error parsing JSON data', { error })
    return {}
  }
}

interface BodyProcessingResult {
  bodyType: 'none' | 'form_data' | 'json' | 'form_urlencoded'
  bodyData: string
}

const isFormDataValue = (value: string): boolean =>
  // More reliable form data detection - check for file-like patterns
  value.includes('@') &&
  (value.includes('.') || // file extensions
    value.includes('/') || // file paths
    value.includes('\\')) // Windows paths

const isValidJson = (str: string): boolean => {
  try {
    JSON.parse(str)
    return true
  } catch {
    return false
  }
}

const parseUrlEncodedData = (data: string): Record<string, string> => {
  const params = new URLSearchParams(data)
  const formData: Record<string, string> = {}
  params.forEach((value, key) => {
    formData[key] = value
  })
  return formData
}

const processObjectData = (data: Record<string, unknown>): BodyProcessingResult => {
  const hasFormData = Object.values(data).some(
    (value) => typeof value === 'string' && isFormDataValue(value),
  )

  const bodyType = hasFormData ? 'form_data' : 'json'
  return { bodyType, bodyData: JSON.stringify(data, null, 2) }
}

const processStringData = (data: string): BodyProcessingResult => {
  // Try to parse as JSON first
  if (isValidJson(data)) {
    return { bodyType: 'json', bodyData: data }
  }

  // If not JSON, treat as form_urlencoded
  return {
    bodyType: 'form_urlencoded',
    bodyData: JSON.stringify(parseUrlEncodedData(data), null, 2),
  }
}

export const processBodyData = (data: ResultJSON['data']): BodyProcessingResult => {
  if (data == null) return { bodyType: 'none', bodyData: '' }

  if (typeof data === 'object' && !Array.isArray(data)) {
    return processObjectData(data)
  }

  if (typeof data === 'string') {
    return processStringData(data)
  }

  // Default fallback for other types (arrays, numbers, etc.)
  return { bodyType: 'json', bodyData: JSON.stringify(data, null, 2) }
}
const isValidUrl = (urlString: string): boolean => {
  try {
    const urlObj = new URL(urlString)
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:'
  } catch {
    return false
  }
}

/**
 * Preprocesses curl command to normalize Postman-generated commands
 * This fixes issues with --location flag and ensures proper URL parsing
 */
export const preprocessCurlCommand = (curlCommand: string): string => {
  let processedCommand = curlCommand.trim()

  // Check if this is a Postman-generated curl (has --location flag)
  const isPostmanCurl = processedCommand.includes('--location')

  if (isPostmanCurl) {
    // Replace --location with -L for better compatibility
    processedCommand = processedCommand.replace(/--location\s+/g, '-L ')

    // Replace --request METHOD with -X METHOD for better compatibility
    processedCommand = processedCommand.replace(/--request\s+(\w+)/g, '-X $1')

    // Clean up line breaks and backslashes first
    processedCommand = processedCommand
      .replace(/\s+\\\s*\n\s*/g, ' ') // Replace backslash line continuations
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim()
    // Extract the URL from the command more reliably
    // Look for the URL pattern after curl -L (and optional -X METHOD) and before the first --header
    const urlMatch = processedCommand.match(
      /curl\s+-L\s+(?:-X\s+\w+\s+)?['"`]?([^'"`\s]+)['"`]?(?:\s+--)/i,
    )
    if (urlMatch) {
      const url = urlMatch[1]

      // Check if the URL is properly formatted
      if (isValidUrl(url)) {
        // If URL is valid, ensure the curl command has proper method detection
        if (!processedCommand.includes('-X')) {
          // Default to GET if no data is present, POST if data is present
          // Note: This is a reasonable default since we can't determine PUT/PATCH/DELETE without explicit method
          const method = processedCommand.includes('--data') ? 'POST' : 'GET'
          // Insert -X METHOD after curl -L and before the URL
          processedCommand = processedCommand.replace(/(curl\s+-L\s+)/i, `$1-X ${method} `)
        }
      } else {
        // If URL parsing fails, try to extract it from quotes
        const quotedUrlMatch = processedCommand.match(/curl\s+-L\s+(['"`])([^'"`]+)\1/)
        if (quotedUrlMatch) {
          const quotedUrl = quotedUrlMatch[2]
          if (isValidUrl(quotedUrl)) {
            // URL is valid, continue with processing
          } else {
            throw new Error('Invalid URL format in curl command')
          }
        } else {
          throw new Error('Could not extract valid URL from curl command')
        }
      }
    } else {
      // Fallback: try to find URL in a different pattern
      const fallbackUrlMatch = processedCommand.match(
        /curl\s+-L\s+(?:-X\s+\w+\s+)?(['"`]?)([^'"`\s]+)\1/,
      )
      if (fallbackUrlMatch) {
        const url = fallbackUrlMatch[2]
        if (!isValidUrl(url)) {
          throw new Error('Invalid URL format in curl command')
        }
      } else {
        throw new Error('Could not extract URL from curl command')
      }
    }
  }

  return processedCommand
}

// Helper function to safely get nested object values (used in http-request/index.tsx :: hasDependentFieldsChanged)
export const getNestedValue = (obj: unknown, path: string): unknown => {
  return path.split('.').reduce((current, key) => {
    if (current && typeof current === 'object' && key in current) {
      return (current as Record<string, unknown>)[key]
    }
    return undefined
  }, obj)
}

// Function to check if dependent fields have changed (used in http-request/index.tsx :: handleBeforeSave)
export const hasDependentFieldsChanged = (
  currentValues: HttpRequestNodeData['config'],
  initialValues: HttpRequestNodeData['config'],
) => {
  const dependentFields = [
    'http_request.url',
    'http_request.method',
    'http_request.auth_type',
    'http_request.auth_params',
    'http_request.params',
    'http_request.headers',
    'http_request.body_type',
    'http_request.body',
  ]

  const changedFields = dependentFields.filter((field) => {
    const currentValue = getNestedValue(currentValues, field)
    const initialValue = getNestedValue(initialValues, field)
    const hasChanged = JSON.stringify(currentValue) !== JSON.stringify(initialValue)
    return hasChanged
  })

  return changedFields.length > 0
}

// Whether saving an HTTP Request node re-runs the API test first. Never in embed: execute_http
// is not allow-listed there, and a failed test blocks the save.
export const shouldTestBeforeSave = (
  currentValues: HttpRequestNodeData['config'],
  initialValues: HttpRequestNodeData['config'],
  embed: boolean,
): boolean => !embed && hasDependentFieldsChanged(currentValues, initialValues)
